import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  activateProviderAdmissionProfile,
  approveProviderAdmissionProfile,
  createProviderAdmissionProfile,
  getProviderAdmissionProfile,
  updateProviderAdmissionProfile,
} from '../../api/providerApi';
import { Modal } from '../../components/ui/Modal';
import { formatDateTime, isUuid } from '../../utils/format';
import { AdmissionProfileForm } from './admissionProfile/AdmissionProfileForm';
import {
  buildAdmissionProfilePayload,
  defaultAdmissionProfilePayload,
  profileToForm,
} from './admissionProfile/admissionProfilePayload';
import { profileStatusView } from './admissionProfile/admissionProfileLabels';

function failureKind(error) {
  if (error?.status !== 404) return 'error';
  const message = String(error?.message || '');
  if (/admission profile not found/i.test(message)) return 'no-profile';
  if (/program not found/i.test(message)) return 'no-program';
  return 'error';
}

function isNetworkError(error) {
  return !error?.status;
}

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

const primaryButtonClass = `inline-flex items-center justify-center rounded-lg bg-[#003A70] px-6 py-3 text-sm font-semibold text-white ${focusRing}`;

const outlineButtonClass = `inline-flex items-center justify-center rounded-lg border border-[#003A70] bg-white px-5 py-2.5 text-sm font-semibold text-[#003A70] ${focusRing}`;

const softButtonClass = `inline-flex items-center justify-center rounded-md bg-[#EEF4FF] px-4 py-2 text-xs font-semibold text-[#003A70] ${focusRing}`;

const panelClass =
  'rounded-2xl border border-[#E6EAF0] bg-white shadow-[6px_6px_54px_rgba(0,0,0,0.03)]';

const STATUS_PILL = {
  DRAFT: 'bg-[#8D8D8D] text-white',
  APPROVED: 'bg-[#FEF3C7] text-[#92400E]',
  ACTIVE: 'bg-[#EAF9F5] text-[#017268]',
  SUPERSEDED: 'bg-[#F3F4F6] text-[#4B5563]',
  RETIRED: 'bg-[#F3F4F6] text-[#4B5563]',
};

function AuditRow({ label, value, mono }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[10rem_1fr] sm:gap-3">
      <dt className="text-xs font-semibold text-[#5A5A5A]">{label}</dt>
      <dd className={mono ? 'break-all font-mono text-sm text-[#1F2933]' : 'text-sm text-[#1F2933]'}>
        {value || '—'}
      </dd>
    </div>
  );
}

function PageAlert({ message, onRetry }) {
  if (!message) return null;
  const denied = /not authorized/i.test(message);
  const conflict = /already ACTIVE/i.test(message);
  const prominent = /Fix the highlighted fields|Not authorized/i.test(message);
  return (
    <section role="alert" className="flex flex-wrap items-center justify-between gap-3">
      <p
        className={
          conflict
            ? 'text-sm font-medium text-[#987200]'
            : prominent
              ? 'text-sm font-bold text-[#832029]'
              : 'text-xs font-semibold text-[#832029]'
        }
      >
        {message}
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className={`text-sm font-semibold ${denied ? 'text-[#DC3545]' : 'text-[#003A70]'} ${focusRing}`}
        >
          Retry
        </button>
      ) : null}
    </section>
  );
}

export function ProgramsRulesPage() {
  const [params, setParams] = useSearchParams();
  const programParam = (params.get('program') || '').trim();
  const programIsValid = isUuid(programParam);

  const [programInput, setProgramInput] = useState(programIsValid ? '' : programParam);
  const [locatorError, setLocatorError] = useState(
    programParam && !programIsValid ? 'Enter a valid programme ID.' : ''
  );
  const [loading, setLoading] = useState(programIsValid);
  const [busy, setBusy] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [missing, setMissing] = useState(null);
  const [error, setError] = useState('');
  const [errorRetry, setErrorRetry] = useState(null);
  const [notice, setNotice] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [dialog, setDialog] = useState(null);
  const busyRef = useRef(false);
  const cancelRef = useRef(null);
  const approveTriggerRef = useRef(null);
  const activateTriggerRef = useRef(null);

  useEffect(() => {
    if (!programIsValid) {
      setLoading(false);
      setProfile(null);
      setForm(null);
      setMissing(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setProfile(null);
    setForm(null);
    setMissing(null);
    setError('');
    setErrorRetry(null);
    setNotice('');
    setFieldErrors({});
    setDialog(null);

    async function load() {
      try {
        const response = await getProviderAdmissionProfile(programParam);
        if (cancelled) return;
        setProfile(response);
        setForm(profileToForm(response));
        setMissing(null);
      } catch (requestError) {
        if (cancelled) return;
        const kind = failureKind(requestError);
        if (kind === 'no-profile' || kind === 'no-program') {
          setMissing(kind);
          setError('');
        } else {
          setMissing(null);
          setError(requestError.message || 'Unable to load the admission profile.');
          setErrorRetry(() => () => setReloadToken((value) => value + 1));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [programIsValid, programParam, reloadToken]);

  function applyProfile(response, nextNotice) {
    setProfile(response);
    setForm(profileToForm(response));
    setMissing(null);
    setFieldErrors({});
    setError('');
    setErrorRetry(null);
    setNotice(nextNotice);
    setDialog(null);
  }

  async function refreshAfterConflict() {
    try {
      const response = await getProviderAdmissionProfile(programParam);
      setProfile(response);
      setForm(profileToForm(response));
      setMissing(null);
    } catch (requestError) {
      setError(requestError.message || 'Unable to reload the admission profile.');
      setErrorRetry(() => () => setReloadToken((value) => value + 1));
    }
  }

  async function runMutation(kind, request, successNotice, retry) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(kind);
    setError('');
    setErrorRetry(null);
    setNotice('');
    try {
      const response = await request();
      applyProfile(response, successNotice(response));
    } catch (requestError) {
      setDialog(null);
      const kindOfFailure = failureKind(requestError);
      if (kindOfFailure === 'no-program') {
        setProfile(null);
        setForm(null);
        setMissing('no-program');
        setError('');
      } else if (kindOfFailure === 'no-profile') {
        setProfile(null);
        setForm(null);
        setMissing('no-profile');
        setError('');
      } else {
        setError(requestError.message || 'Request failed.');
        if (requestError.status === 409) {
          await refreshAfterConflict();
        } else if (isNetworkError(requestError)) {
          setErrorRetry(() => retry || (() => setReloadToken((value) => value + 1)));
        }
      }
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  function submitDraft(purpose) {
    if (!form) return;
    const result = buildAdmissionProfilePayload(form);
    if (!result.ok) {
      setFieldErrors(result.errors);
      setNotice('');
      setError('Fix the highlighted fields before saving.');
      setErrorRetry(null);
      return;
    }
    setFieldErrors({});
    if (purpose === 'create-version') {
      runMutation(
        'create',
        () => createProviderAdmissionProfile(programParam, result.payload),
        (response) =>
          `Draft version ${response.version} created. It is being prepared and is not live.`
      );
      return;
    }
    runMutation(
      'save',
      () => updateProviderAdmissionProfile(profile.id, result.payload),
      () => 'Draft saved.'
    );
  }

  function createDraft() {
    runMutation(
      'create',
      () => createProviderAdmissionProfile(programParam, defaultAdmissionProfilePayload()),
      () => 'Draft created. It is not used for new submissions.',
      () => createDraft()
    );
  }

  function confirmApprove() {
    runMutation(
      'approve',
      () => approveProviderAdmissionProfile(profile.id),
      () => 'Approved — not live.'
    );
  }

  function confirmActivate() {
    runMutation(
      'activate',
      () => activateProviderAdmissionProfile(profile.id),
      (response) => `Version ${response.version} is the active programme policy.`
    );
  }

  function loadProgramme(event) {
    event.preventDefault();
    const value = programInput.trim();
    if (!isUuid(value)) {
      setLocatorError('Enter a valid programme ID.');
      return;
    }
    setLocatorError('');
    setParams({ program: value });
  }

  function clearProgramme() {
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('program');
      return next;
    });
  }

  if (!programIsValid) {
    return (
      <div className="space-y-6">
        <section className={`${panelClass} p-8`}>
          <h2 className="text-xl font-semibold tracking-[-0.01em] text-[#1F2933]">
            Programme configuration
          </h2>
          <form className="mt-6 flex flex-col gap-6" onSubmit={loadProgramme}>
            <div className="flex flex-col gap-2">
              <label htmlFor="programme-id" className="text-xs font-semibold text-[#1F2933]">
                Programme ID
              </label>
              <input
                id="programme-id"
                value={programInput}
                autoComplete="off"
                spellCheck={false}
                placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                aria-invalid={locatorError ? true : undefined}
                aria-describedby={
                  locatorError ? 'programme-id-help programme-id-error' : 'programme-id-help'
                }
                className="block w-full rounded-lg border border-[#AAC2FE] bg-white px-3.5 py-2.5 text-sm text-[#1F2933] placeholder:text-[#8D8D8D] focus:border-[#003A70] focus:outline-none focus:ring-2 focus:ring-[#003A70]/15"
                onChange={(event) => {
                  setProgramInput(event.target.value);
                  if (locatorError) setLocatorError('');
                }}
              />
            </div>
            {locatorError ? (
              <p id="programme-id-error" className="text-xs text-[#DC3545]" role="alert">
                {locatorError}
              </p>
            ) : null}
            <div id="programme-id-help" className="flex flex-col gap-3 text-xs leading-4 text-[#5A5A5A]">
              <p>Configure the admission policy used for subsequent submissions to this programme.</p>
              <p>This screen configures programme policy. It does not review or admit individual packages.</p>
            </div>
            <div>
              <button type="submit" className={primaryButtonClass}>
                Load
              </button>
            </div>
          </form>
        </section>
      </div>
    );
  }

  const status = profileStatusView(profile?.status);
  const editable = profile?.status === 'DRAFT' && !busy && !loading;
  const dialogCopy =
    dialog === 'activate'
      ? {
          title: 'Activate this programme profile?',
          lines: [
            `Version ${profile?.version} will become the active programme policy for subsequent submissions. If another version is active, it becomes historical.`,
            'Packages that already have a programme profile snapshot keep their existing version and policy hash.',
            'This does not approve a package, authorize custody, or admit a record.',
          ],
          confirm: busy === 'activate' ? 'Activating…' : 'Activate profile',
          onConfirm: confirmActivate,
        }
      : dialog === 'approve'
        ? {
            title: 'Approve this programme profile?',
            lines: [
              `This will move Version ${profile?.version} from Draft to Approved. It will not make the profile active or change the policy used for new submissions.`,
            ],
            confirm: busy === 'approve' ? 'Approving…' : 'Approve profile',
            onConfirm: confirmApprove,
          }
        : null;

  const denied = /not authorized/i.test(error);

  return (
    <div className="space-y-6" aria-busy={loading || Boolean(busy)}>
      {error ? <PageAlert message={error} onRetry={errorRetry || undefined} /> : null}

      <section className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-[#F1F1F1] bg-white p-5 shadow-[6px_6px_54px_rgba(0,0,0,0.03)]">
        <div className="min-w-0 space-y-1.5">
          <h2 className="text-xl font-semibold text-[#003A70]">Programme Admission Profile</h2>
          <p className="text-sm text-[#5A5A5A]">
            Programme policy configuration for Document Admission.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-1.5">
            <p className="rounded bg-[#F4F7FA] px-2 py-1 text-[11px] leading-4 text-[#16335D]">
              Programme ID: {programParam}
            </p>
            <p className="max-w-3xl text-xs leading-4 text-[#5A5A5A]">
              This configuration defines the programme policy used when submissions are evaluated. It is
              not a package decision, custody authorization, or vault admission.
            </p>
          </div>
        </div>
        <button
          type="button"
          className={softButtonClass}
          disabled={Boolean(busy) || loading}
          onClick={clearProgramme}
        >
          Change programme
        </button>
      </section>

      {loading ? (
        <p className="text-sm font-medium text-[#1F2933]">Loading admission profile…</p>
      ) : null}

      {!loading && missing === 'no-program' ? (
        <section className={`${panelClass} p-6`}>
          <PageAlert
            message="Programme not found"
            onRetry={() => setReloadToken((value) => value + 1)}
          />
          <h3 className="mt-4 text-lg font-bold text-[#1F2933]">Programme not found</h3>
          <p className="mt-2 text-sm text-[#5A5A5A]">
            No programme exists for this ID. Check the programme ID and try again.
          </p>
        </section>
      ) : null}

      {!loading && missing === 'no-profile' ? (
        <section className={`${panelClass} p-6`}>
          <h3 className="text-xl font-semibold text-[#1F2933]">No Admission Profile</h3>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#5A5A5A]">
            This programme does not have an Admission Profile yet. Create a draft to configure the
            programme policy used for subsequent submissions.
          </p>
          <div className="mt-4">
            <button
              type="button"
              className={primaryButtonClass}
              onClick={createDraft}
              disabled={Boolean(busy)}
            >
              {busy === 'create' ? 'Creating…' : 'Create draft'}
            </button>
          </div>
        </section>
      ) : null}

      {!loading && denied ? (
        <section className={`${panelClass} p-6`}>
          <h3 className="text-sm font-medium text-[#5A5A5A]">Access Denied</h3>
          <p className="mt-2 max-w-3xl text-xs leading-4 text-[#5A5A5A]">
            You do not have the required administrative permissions to modify policies for this
            Programme ID. Contact your workspace administrator.
          </p>
        </section>
      ) : null}

      {!loading && profile && form ? (
        <>
          <section className={`${panelClass} flex flex-wrap items-start justify-between gap-4 p-6`}>
            <div role="status" aria-live="polite" className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-lg font-bold text-[#1F2933]">Version {profile.version}</p>
                <span
                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    STATUS_PILL[profile.status] || 'bg-[#F3F4F6] text-[#4B5563]'
                  }`}
                >
                  {status.label}
                </span>
              </div>
              <p className="max-w-3xl text-sm text-[#5A5A5A]">{status.description}</p>
              {notice ? (
                <p className="inline-flex rounded-md bg-[#EAF9F5] px-3 py-2 text-xs font-semibold text-[#017268]">
                  {notice}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.status === 'DRAFT' ? (
                <button
                  ref={approveTriggerRef}
                  type="button"
                  className={outlineButtonClass}
                  disabled={Boolean(busy)}
                  onClick={() => setDialog('approve')}
                >
                  Approve profile
                </button>
              ) : null}
              {profile.status === 'APPROVED' ? (
                <button
                  ref={activateTriggerRef}
                  type="button"
                  className={outlineButtonClass}
                  disabled={Boolean(busy)}
                  onClick={() => setDialog('activate')}
                >
                  Activate profile
                </button>
              ) : null}
              {profile.status === 'ACTIVE' ? (
                <button
                  type="button"
                  className={outlineButtonClass}
                  disabled={Boolean(busy)}
                  onClick={() => submitDraft('create-version')}
                >
                  {busy === 'create' ? 'Creating…' : 'Create next version'}
                </button>
              ) : null}
            </div>
          </section>

          <AdmissionProfileForm
            form={form}
            errors={fieldErrors}
            disabled={!editable}
            showSave={profile.status === 'DRAFT'}
            saveLabel={busy === 'save' ? 'Working…' : 'Save draft'}
            onChange={setForm}
            onSubmit={() => submitDraft('save')}
          />

          <details className={`${panelClass} p-6`}>
            <summary className={`cursor-pointer text-base font-bold text-[#1F2933] ${focusRing}`}>
              Audit
            </summary>
            <dl className="mt-4 space-y-3">
              <AuditRow label="Version" value={String(profile.version)} />
              <AuditRow label="Policy hash" value={profile.policy_hash} mono />
              <AuditRow label="Created by" value={profile.created_by} mono />
              <AuditRow label="Created at" value={formatDateTime(profile.created_at)} />
              {profile.approved_by || profile.approved_at ? (
                <>
                  <AuditRow label="Approved by" value={profile.approved_by} mono />
                  <AuditRow label="Approved at" value={formatDateTime(profile.approved_at)} />
                </>
              ) : null}
              {profile.activated_by || profile.activated_at ? (
                <>
                  <AuditRow label="Activated by" value={profile.activated_by} mono />
                  <AuditRow label="Activated at" value={formatDateTime(profile.activated_at)} />
                </>
              ) : null}
            </dl>
          </details>
        </>
      ) : null}

      <Modal
        open={Boolean(dialogCopy)}
        onClose={() => {
          if (busy) return;
          setDialog(null);
        }}
        title={dialogCopy?.title}
        size="lg"
        initialFocusRef={cancelRef}
        titleClassName="text-xl font-semibold text-[#1F2933]"
        headerClassName="px-6 pb-2 pt-6"
        footerClassName="flex justify-end gap-3 bg-white px-6 py-5"
        footer={
          dialogCopy ? (
            <>
              <button
                ref={cancelRef}
                type="button"
                className={outlineButtonClass}
                disabled={Boolean(busy)}
                onClick={() => setDialog(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={primaryButtonClass}
                disabled={Boolean(busy)}
                onClick={dialogCopy.onConfirm}
              >
                {dialogCopy.confirm}
              </button>
            </>
          ) : null
        }
      >
        {dialogCopy
          ? dialogCopy.lines.map((line) => (
              <p key={line} className="mb-2 text-sm leading-5 text-[#5A5A5A] last:mb-0">
                {line}
              </p>
            ))
          : null}
      </Modal>
    </div>
  );
}
